import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { sound } from "../systems/Sound";
import { THEME, button, panel, type ButtonOptions, type PanelVariant } from "./theme";

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const MENU_SAFE_MARGIN = 24;
export const MENU_HEADER_HEIGHT = 66;
export const MENU_CONTENT: Bounds = {
  x: MENU_SAFE_MARGIN,
  y: 96,
  width: GAME_WIDTH - MENU_SAFE_MARGIN * 2,
  height: GAME_HEIGHT - 116,
};

export interface SceneLifecycle {
  add(cleanup: () => void): void;
  key(event: string, handler: (...args: unknown[]) => void): void;
  input(event: string, handler: (...args: unknown[]) => void): void;
  dispose(): void;
}

/**
 * Owns listeners that Phaser does not automatically remove when a Scene is
 * stopped and later reused. Calling this at the beginning of every menu
 * Scene makes repeated enter/leave cycles deterministic.
 */
export function beginSceneLifecycle(scene: Phaser.Scene): SceneLifecycle {
  const cleanups: Array<() => void> = [];
  let disposed = false;

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    for (const cleanup of cleanups.splice(0).reverse()) {
      try { cleanup(); } catch { /* stale Phaser objects are safe to ignore */ }
    }
  };

  const onShutdown = () => dispose();
  const onDestroy = () => dispose();
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, onShutdown);
  scene.events.once(Phaser.Scenes.Events.DESTROY, onDestroy);
  cleanups.push(() => scene.events.off(Phaser.Scenes.Events.SHUTDOWN, onShutdown));
  cleanups.push(() => scene.events.off(Phaser.Scenes.Events.DESTROY, onDestroy));

  return {
    add(cleanup) {
      if (disposed) cleanup();
      else cleanups.push(cleanup);
    },
    key(event, handler) {
      scene.input.keyboard?.on(event, handler);
      cleanups.push(() => scene.input.keyboard?.off(event, handler));
    },
    input(event, handler) {
      scene.input.on(event, handler);
      cleanups.push(() => scene.input.off(event, handler));
    },
    dispose,
  };
}

export interface MenuBackdropOptions {
  darkness?: number;
  texture?: string;
  parallax?: boolean;
}

/** Full-bleed painted lobby backdrop with a restrained iron vignette. */
export function addMenuBackdrop(
  scene: Phaser.Scene,
  options: MenuBackdropOptions = {},
): Phaser.GameObjects.Image | null {
  const texture = options.texture ?? "bg-lobby";
  let image: Phaser.GameObjects.Image | null = null;

  scene.cameras.main.setBackgroundColor(THEME.hex.ironDeep);
  if (scene.textures.exists(texture)) {
    image = scene.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, texture).setOrigin(0.5);
    const cover = Math.max(GAME_WIDTH / image.width, GAME_HEIGHT / image.height);
    image.setScale(cover * (options.parallax ? 1.018 : 1));
    if (options.parallax) {
      scene.tweens.add({
        targets: image,
        x: { from: GAME_WIDTH / 2 - 6, to: GAME_WIDTH / 2 + 6 },
        y: { from: GAME_HEIGHT / 2 - 2, to: GAME_HEIGHT / 2 + 2 },
        duration: 12_000,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
    }
  } else {
    scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, THEME.hex.ironDeep).setOrigin(0);
  }

  scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x080502, options.darkness ?? 0.72).setOrigin(0);
  scene.add.rectangle(0, 0, GAME_WIDTH, 88, THEME.hex.ironDeep, 0.58).setOrigin(0);
  scene.add.rectangle(0, GAME_HEIGHT - 42, GAME_WIDTH, 42, THEME.hex.ironDeep, 0.58).setOrigin(0);

  const trim = scene.add.graphics();
  trim.lineStyle(2, THEME.hex.goldDim, 0.72);
  trim.lineBetween(MENU_SAFE_MARGIN, 86, GAME_WIDTH - MENU_SAFE_MARGIN, 86);
  trim.lineStyle(1, THEME.hex.ironEdge, 0.45);
  trim.lineBetween(MENU_SAFE_MARGIN, GAME_HEIGHT - 42, GAME_WIDTH - MENU_SAFE_MARGIN, GAME_HEIGHT - 42);
  return image;
}

export interface CastleBackdropOptions {
  /** "parchment" brightens for dark-on-light content (Achievements); "dark" keeps light text readable. */
  veil?: "parchment" | "dark";
  /** Art opacity; defaults per veil (0.5 parchment, 0.26 dark). */
  alpha?: number;
  /** Disables panel clipping mask when caller already applies its own viewport mask. */
  mask?: boolean;
  /** Optional container target for scrollable content backdrops. */
  parent?: Phaser.GameObjects.Container;
  /** Optional insert index used only when parent is provided. */
  insertAt?: number;
}

/**
 * Fills a content-panel rect with the castle art, cover-scaled and clipped so
 * it never bleeds past it, plus a veil so text and cards stay dominant.
 * Call right after addContentPanel, before drawing panel content.
 */
export function addCastleBackdrop(
  scene: Phaser.Scene,
  bounds: Bounds = MENU_CONTENT,
  options: CastleBackdropOptions = {},
): void {
  if (!scene.textures.exists("bg-castle")) return;
  const veil = options.veil ?? "parchment";

  const bg = scene.add.image(bounds.x, bounds.y, "bg-castle")
    .setOrigin(0, 0)
    .setAlpha(options.alpha ?? (veil === "parchment" ? 0.5 : 0.26));
  const cover = Math.max(bounds.width / bg.width, bounds.height / bg.height);
  bg.setScale(cover);

  if (options.mask !== false) {
    const maskShape = scene.add.graphics().setVisible(false);
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
    bg.setMask(maskShape.createGeometryMask());
  }

  let veilRect: Phaser.GameObjects.Rectangle;
  if (veil === "parchment") {
    veilRect = scene.add.rectangle(bounds.x, bounds.y, bounds.width, bounds.height, 0xf2e2b8, 0.35).setOrigin(0, 0);
  } else {
    veilRect = scene.add.rectangle(bounds.x, bounds.y, bounds.width, bounds.height, 0x0d0803, 0.42).setOrigin(0, 0);
  }

  if (options.parent) {
    const index = options.insertAt;
    if (typeof index === "number") {
      options.parent.addAt(bg, index);
      options.parent.addAt(veilRect, Math.min(index + 1, options.parent.length));
    } else {
      options.parent.add([bg, veilRect]);
    }
  }
}

export interface MenuHeaderOptions {
  title: string;
  subtitle?: string;
  onBack?: () => void;
  backLabel?: string;
  rightLabel?: string;
  onRight?: () => void;
}

export function addMenuHeader(scene: Phaser.Scene, options: MenuHeaderOptions): void {
  panel(scene, MENU_SAFE_MARGIN, 14, GAME_WIDTH - MENU_SAFE_MARGIN * 2, MENU_HEADER_HEIGHT, "iron-bar");

  if (options.onBack) {
    menuButton(scene, 104, 47, 130, 38, options.backLabel ?? "‹ MENU", {
      name: "menu-back",
      fontSize: 15,
      onClick: options.onBack,
    });
  }

  scene.add.text(GAME_WIDTH / 2, options.subtitle ? 37 : 47, options.title, {
    fontFamily: THEME.fonts.display,
    fontSize: options.subtitle ? "30px" : "34px",
    color: THEME.colors.gold,
    stroke: THEME.colors.ironDeep,
    strokeThickness: 3,
  }).setOrigin(0.5);

  if (options.subtitle) {
    scene.add.text(GAME_WIDTH / 2, 64, options.subtitle, {
      fontFamily: THEME.fonts.body,
      fontSize: "14px",
      color: THEME.colors.parchmentDim,
    }).setOrigin(0.5);
  }

  if (options.rightLabel && options.onRight) {
    menuButton(scene, GAME_WIDTH - 104, 47, 130, 38, options.rightLabel, {
      name: "menu-header-action",
      fontSize: 14,
      onClick: options.onRight,
    });
  }
}

/** Adds the standard top-right X used to leave an opened screen. */
export function addQuitButton(
  scene: Phaser.Scene,
  onClick: () => void = () => scene.scene.start("MenuScene"),
  x = GAME_WIDTH - 54,
  y = 47,
): Phaser.GameObjects.Container {
  return menuButton(scene, x, y, 48, 38, "X", {
    name: "screen-quit",
    fontSize: 18,
    color: THEME.colors.parchment,
    onClick,
  }).setDepth(THEME.depths.overlay + 10);
}

export interface MenuButtonOptions extends Omit<ButtonOptions, "onClick"> {
  name?: string;
  enabled?: boolean;
  onClick: () => void;
}

export function menuButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  label: string,
  options: MenuButtonOptions,
): Phaser.GameObjects.Container {
  const enabled = options.enabled ?? true;
  const wrapped = () => {
    if (!enabled) return;
    sound.playUi("click");
    options.onClick();
  };
  const result = button(scene, x, y, width, height, label, {
    ...options,
    color: enabled ? options.color : THEME.colors.lock,
    onClick: wrapped,
  });
  result.setName(options.name ?? `menu-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`);
  if (!enabled) {
    result.setAlpha(0.58);
    const background = result.getData("bg") as Phaser.GameObjects.NineSlice | undefined;
    background?.disableInteractive();
  }
  return result;
}

export function addContentPanel(
  scene: Phaser.Scene,
  bounds: Bounds = MENU_CONTENT,
  variant: PanelVariant = "iron",
  alpha = 0.96,
): Phaser.GameObjects.NineSlice {
  return panel(scene, bounds.x, bounds.y, bounds.width, bounds.height, variant).setAlpha(alpha);
}

export function addEmptyState(
  scene: Phaser.Scene,
  x: number,
  y: number,
  title: string,
  detail: string,
  width = 520,
): void {
  scene.add.text(x, y, title, {
    fontFamily: THEME.fonts.display,
    fontSize: "22px",
    color: THEME.colors.parchment,
    align: "center",
  }).setOrigin(0.5);
  scene.add.text(x, y + 34, detail, {
    fontFamily: THEME.fonts.body,
    fontSize: "16px",
    color: THEME.colors.parchmentDim,
    align: "center",
    wordWrap: { width },
  }).setOrigin(0.5, 0);
}

export function addPageFooter(scene: Phaser.Scene, text: string): void {
  scene.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 20, text, {
    fontFamily: THEME.fonts.body,
    fontSize: "12px",
    color: THEME.colors.lock,
  }).setOrigin(0.5);
}
